import { UserInfo } from "./canonical";

// --- Constants for Filters ---
export const POSSIBLE_USER_STATUSES = [
  "Open",
  "Completed",
  "Investigating",
  "Not Relevant",
  "Duplicate",
  "AI Generated",
] as const;
export type UserStatus = (typeof POSSIBLE_USER_STATUSES)[number];

export const POSSIBLE_PIPELINE_STATUSES = [
  "AI Generated",
  "Needs Article",
  "Processing",
  "Implemented",
  "Manually Resolved",
  "Archived",
] as const;
export type PipelineStatus = (typeof POSSIBLE_PIPELINE_STATUSES)[number];

// --- Interfaces ---
export interface UserAddedMissingArticle {
  title: string;
  description: string;
  priority: string;
}

export interface GeneratedContentLanguageEntry {
  content?: string | null;
  thoughts?: string | null;
  error?: string | null;
  input_prompt?: string | null;
  timestamp: string;
  generated_by?: { username: string; timestamp: string } | null;
  metadata?: AILanguageCallMetadata | null;
}

export interface GeneratedContentVersion {
  version: number;
  languages: Record<string, GeneratedContentLanguageEntry>;
}

export interface GeneratedContentSchema {
  versions: GeneratedContentVersion[];
}

export interface MissingArticleItemSchema {
  id?: string;
  _id: string;
  release_note_id: string | null;
  release_note_title?: string;
  release_note_version?: string | null;
  release_note_url?: string | null;
  title?: string | null;
  title_override?: string | null;
  description?: string | null;
  description_html?: string | null;
  priority?: string | null;
  section_title?: string | null;
  release_item_version?: string | null;
  release_item_url?: string | null;
  release_item_categories: string[];
  db_last_updated: string;
  run_id?: string | null;
  user_status: UserStatus;
  pipeline_status: PipelineStatus;
  status_last_updated_by_id?: string | null;
  status_last_updated_by_name?: string | null;
  status_last_updated_at?: string | null;
  user_notes?: string | null;
  generated_content?: GeneratedContentSchema | null;
  db_created_at: string;
  manual_creation: boolean;
  created_by?: UserInfo;
}

// Corresponds to backend's PaginatedMissingArticlesSchema
export interface PaginatedMissingArticles {
  total_items: number;
  items: MissingArticleItemSchema[];
  page: number;
  page_size: number;
  total_pages: number;
}

export interface MissingArticlesProps {
  apiBaseUrl: string;
}

export interface UploadedFile {
  id: string;
  file: File;
}

export interface UserAddedMissingArticlePayload {
  title: string;
  description: string;
  priority: string;
  section_title: string;
  manual_creation: boolean;
}

export interface MissingArticleDeletePayload {
    id?: string;
    _id?: string;
    title: string;
    title_override?: string | null
}

export interface UserAddedMissingArticle {
  id?: string;
  _id?: string;
  title: string;
  title_override?: string | null;
  description: string;
  priority: string;
  user_status: UserStatus;
  pipeline_status: PipelineStatus;
  status_last_updated_by_id?: string | null;
  status_last_updated_by_name?: string | null;
  status_last_updated_at?: string | null;
  user_notes?: string | null;
  manual_creation: boolean;
  created_by?: UserInfo;
  generated_content?: GeneratedContentSchema | null;
  db_created_at?: string;
}

export interface MissingArticleTitleOverridePayload {
  _id: string;
  title_override: string;
}

export interface MissingArticleTitleRevertRequest {
  _id: string;
}

// --- START: Interfaces for AI Call Metadata (mirroring Python Schemas) ---

export interface GeminiPart {
  type: string;
  text?: string | null;
  mime_type?: string | null;
  data_length?: number | null;
  thought?: boolean | null;
}

export interface ThinkingConfig {
  include_thoughts: boolean;
}

// --- Tool Detail Schemas (mirroring Python) ---
export interface FunctionDeclaration {
  name: string;
  description?: string | null;
}

export interface GoogleSearch {
  // Empty, marker type
}

export interface UrlContext {
  // Empty, marker type
}

export interface VertexAISearch {
  datastore: string;
}

export interface VertexRagStoreRagResource {
  rag_corpus?: string | null;
  rag_file_ids?: string[] | null;
}

export interface VertexRagStore {
  rag_resources?: VertexRagStoreRagResource[] | null;
}

export interface Retrieval {
  vertex_ai_search?: VertexAISearch | null;
  vertex_rag_store?: VertexRagStore | null;
  disable_attribution?: boolean | null;
}

export interface GoogleSearchRetrieval {
  // Potentially similar to Retrieval or a marker
}

export interface ToolCodeExecution {
  // For code execution tool details
}

export interface GeminiTool {
  function_declarations?: FunctionDeclaration[] | null;
  retrieval?: Retrieval | null;
  google_search?: GoogleSearch | null;
  google_search_retrieval?: GoogleSearchRetrieval | null;
  code_execution?: ToolCodeExecution | null;
  raw_tool_details?: Record<string, any> | null;
}

export interface GeminiGenerateContentConfig {
  temperature?: number | null;
  top_p?: number | null;
  top_k?: number | null;
  candidate_count?: number | null;
  max_output_tokens?: number | null;
  stop_sequences?: string[] | null;
  tools?: GeminiTool[] | null;
  thinking_config?: ThinkingConfig | null;
}

export interface AIRequest {
  model_id: string;
  prompt_parts: GeminiPart[];
  generation_config: GeminiGenerateContentConfig;
  system_instruction_text?: string | null;
}

export interface GeminiSafetyRating {
  category: string;
  probability: string;
  blocked?: boolean | null;
}

export interface GeminiPromptFeedback {
  block_reason?: string | null;
  block_reason_message?: string | null;
  safety_ratings?: GeminiSafetyRating[] | null;
}

export interface GeminiContent {
  parts: GeminiPart[];
  role?: string | null;
}

export interface GeminiCandidate {
  content?: GeminiContent | null;
  finish_reason?: string | null;
  safety_ratings?: GeminiSafetyRating[] | null;
  token_count?: number | null;
}

export interface GeminiUsageMetadata {
  prompt_token_count?: number | null;
  candidates_token_count?: number | null;
  total_token_count?: number | null;
  thoughts_token_count?: number | null;
  tool_use_prompt_token_count?: number | null;
  cached_content_token_count?: number | null;
  response_token_count?: number | null;
  traffic_type?: string | null;
}

export interface AIResponse {
  text?: string | null;
  candidates?: GeminiCandidate[] | null;
  prompt_feedback?: GeminiPromptFeedback | null;
  usage_metadata?: GeminiUsageMetadata | null;
}

export interface AILanguageCallMetadata {
  request_timestamp: string;
  request_config: AIRequest;
  response_details?: AIResponse | null;
  api_call_error?: string | null;
  stream_end_timestamp?: string | null;
}

// --- END: Interfaces for AI Call Metadata ---

// --- Translation Request Schema (Frontend representation of Backend Schema) ---
export interface TranslateRequestSchema {
  version: number;
  from_language: string;
  to_language: string;
  content: string;
  enable_url_context?: boolean; // Optional in backend, match for flexibility
  enable_google_search?: boolean; // Optional in backend, match for flexibility
}

